<template>
  <BInput
    :id="instanceId"
    ref="PhoneNumberInput"
    :model-value="formattedNumber"
    :placeholder="inputPlaceholder"
    :disabled="disabled"
    v-bind="$attrs"
    :size="size"
    :large="large"
    :small="small"
    type="tel"
    clearable
    prefix-class="is-select"
    :class="[ns.b(), ns.is('no-flags', noFlags), ns.is('focus', inputFocused)]"
    data-test-name="phone-input"
    data-test-variant=""
    data-test-state=""
    @focus="inputFocused = true"
    @blur="onBlur"
    @update:model-value="emitsValueAndResults($event)"
    @keydown="onKeydown($event)"
  >
    <template #prefix>
      <BSelect
        v-if="!noCountrySelector && countryOptions"
        ref="CountrySelector"
        :class="[ns.e('select'), ns.is('no-country-code', !countryCode)]"
        :model-value="countryCode"
        :disabled="disabled"
        :size="size"
        :large="large"
        :small="small"
        :placeholder="''"
        @update:model-value="setCountryCode($event as CountryCode, true)"
        @focus="inputFocused = true"
      >
        <template
          v-if="countryCode && !noFlags && !noCountrySelector"
          #selected="{ value }"
        >
          <div class="b-flag__wrapper">
            <div class="b-flag" :class="`b-flag-${value.toLowerCase()}`" />
          </div>
        </template>
        <template v-if="showCountriesFilter" #dropdownHeader>
          <BInput
            v-model="filterValue"
            small
            placeholder="Filter by name"
            @focus="inputFocused = true"
          />
        </template>
        <BOption
          v-for="item in countryOptions"
          :key="item.iso2"
          :value="item.iso2"
          :class="ns.e('select-option')"
        >
          <span class="b-flag__wrapper">
            <span
              v-if="!noFlags && typeof item.iso2 === 'string'"
              class="b-flag"
              :class="[`b-flag-${item.iso2.toLowerCase()}`]"
            />
          </span>

          <span :class="ns.e('select-option-name')">
            {{ item.name }}
          </span>
          <span v-if="showCodeOnList" :class="ns.e('select-option-code')">
            ({{ item.dialCode }})
          </span>
        </BOption>
      </BSelect>
    </template>
  </BInput>
</template>

<script lang="ts" setup>
import {
  computed,
  getCurrentInstance,
  onBeforeMount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { CountryCode } from 'libphonenumber-js'
import { BInput } from '@bigin/components/input'
import { BOption, BSelect } from '@bigin/components/select'
import { useNamespace } from '@bigin/hooks'
import locales from './constants/locales'
import {
  browserLocale,
  fetchCountryCode,
  getAsYouTypeFormat,
  getCountriesList,
  getExamplePhoneNumber,
  getResultsFromPhoneNumber,
  isCountryAvailable,
  loadPhoneNumberExamplesFile,
  sanitizePhoneNumber,
  truthyFilter,
  useInstanceUniqId,
} from './utils'
import { phoneInputEmits, phoneInputProps } from './phone-input'
import type { Result } from './types'

defineOptions({ name: 'BPhoneInput', components: { BInput, BSelect, BOption } })

const props = defineProps(phoneInputProps)
const emits = defineEmits(phoneInputEmits)
const ns = useNamespace('phone-input')

const instance = getCurrentInstance()
const { instanceId } = useInstanceUniqId({
  componentName: 'BInput',
  instance,
  providedId: props.id,
})

const results = ref<Partial<Result>>({})
const countryCode = ref<CountryCode>()
const formattedNumber = ref<string>()
const cursorPosition = ref<number | null>()
const examplesFileLoaded = ref(false)
const inputFocused = ref(false)
const lastKeyPressed = ref<KeyboardEvent['key']>()
const CountrySelector = ref<typeof BSelect>()
const PhoneNumberInput = ref<typeof BInput>()

onBeforeMount(async () => {
  try {
    formattedNumber.value = props.modelValue ?? props.defaultPhoneNumber

    if (props.defaultCountryCode) {
      setCountryCode(props.defaultCountryCode)
    }

    if (props.fetchCountry) {
      const locale = await fetchCountryCode()
      if (locale) setCountryCode(locale)
    }

    getAndEmitResults(formattedNumber.value)
  } catch (error) {
    throw new Error(`[BPhoneInput](onBeforeMount) ${error}`)
  }

  try {
    if (!props.noExample && !examplesFileLoaded.value) {
      await loadPhoneNumberExamplesFile()
      examplesFileLoaded.value = true
    }
  } catch {
    throw new Error(
      '[BPhoneInput](onBeforeMount) while loading phone number examples file'
    )
  }
})

onMounted(() => {
  try {
    if (
      !props.defaultCountryCode &&
      !props.noUseBrowserLocale &&
      !countryCode.value
    ) {
      const locale = browserLocale()
      if (locale) {
        setCountryCode(locale)
      }
    }

    if (props.defaultCountryCode && props.fetchCountry) {
      throw String(
        "Do not use 'fetch-country' and 'default-country-code' options in the same time"
      )
    }
    if (props.defaultCountryCode && props.noUseBrowserLocale) {
      throw String(
        "If you use a 'default-country-code', do not use 'no-use-browser-locale' options"
      )
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`[BPhoneInput](mounted) ${error}`)
  }
})

const countries = computed(() => getCountriesList(props.customCountriesList))

const t = computed(() => ({
  ...locales,
  ...props.translations,
}))

const filterValue = ref('')

const isValid = computed(() => {
  return results.value?.isValid
})

const countriesList = computed(() => {
  return countries.value?.filter(
    (item) => !props.ignoredCountries?.includes(item.iso2)
  )
})

const countriesFiltered = computed(() => {
  const countries = props.onlyCountries || props.preferredCountries
  return countries?.map((country) =>
    countriesList.value?.find((item) => item.iso2.includes(country))
  )
})

const otherCountries = computed(() => {
  return countriesList.value?.filter(
    (item) => !props.preferredCountries?.includes(item.iso2)
  )
})

const countriesSorted = computed(() => {
  return props.preferredCountries
    ? [...(countriesFiltered.value ?? []), ...(otherCountries.value ?? [])]
    : props.onlyCountries
    ? countriesFiltered.value
    : countriesList.value
})

const countryOptions = computed(() => {
  return countriesSorted.value
    ?.map((country) => {
      return country
        ? {
            ...country,
            dialCode: `+${country.dialCode}`,
          }
        : undefined
    })
    .filter((i) =>
      i?.name.toLowerCase().includes(filterValue.value.toLowerCase())
    )
    .filter(truthyFilter)
})

const inputPlaceholder = computed(() => {
  if (props.placeholder) {
    return props.placeholder
  }

  const defaultPlaceholder = t.value.phoneInput.placeholder

  if (props.noExample || !examplesFileLoaded.value) {
    return defaultPlaceholder
  } else {
    const example = getPhoneNumberExample()
    return isValid.value || !example
      ? defaultPlaceholder
      : `${t.value.phoneInput.example} ${example}`
  }
})

watch(
  () => props.modelValue,
  (phoneNumber, oldPhoneNumber) => {
    if (phoneNumber !== oldPhoneNumber) {
      emitsValueAndResults(phoneNumber)
    }
  }
)

watch(
  () => props.defaultPhoneNumber,
  (phoneNumber, oldPhoneNumber) => {
    if (phoneNumber !== oldPhoneNumber) {
      emitsValueAndResults(phoneNumber)
    }
  }
)

watch(
  () => props.defaultCountryCode,
  (countryCode, oldCountryCode) => {
    if (countryCode && countryCode !== oldCountryCode) {
      setCountryCode(countryCode)
      emitsValueAndResults()
    }
  }
)

const onKeydown = (event: KeyboardEvent) => {
  lastKeyPressed.value = event.key

  const target = event.target as HTMLInputElement | undefined

  cursorPosition.value = target?.selectionStart
}

const getPhoneNumberExample = () => {
  try {
    const phoneNumber = countryCode.value
      ? getExamplePhoneNumber(countryCode.value)
      : undefined
    return phoneNumber ? phoneNumber.formatNational() : undefined
  } catch (error) {
    throw new Error(`[BPhoneInput](getPhoneNumberExample) ${error}`)
  }
}

const autoUpdateCountryCodeFromPhoneNumber = () => {
  if (
    results.value &&
    results.value.countryCode &&
    countryCode.value !== results.value.countryCode
  ) {
    setCountryCode(results.value.countryCode)
  }
}

const sanitizeNumber = (phoneNumber?: string) => {
  phoneNumber = sanitizePhoneNumber(phoneNumber)

  const backSpacePressed = lastKeyPressed.value === 'Backspace'

  const lastCharacOfPhoneNumber = phoneNumber
    ? phoneNumber.charAt(phoneNumber.length - 1)
    : ''
  const lastCharIsParanthese = lastCharacOfPhoneNumber === ')'

  if (backSpacePressed && lastCharIsParanthese) {
    phoneNumber = phoneNumber?.trim().slice(0, -2)
  }

  return phoneNumber
}

const getAndEmitResults = (
  phoneNumber?: string,
  noAutoUpdateCountryCode?: boolean
) => {
  results.value = getResultsFromPhoneNumber(countryCode.value, phoneNumber)

  if (!noAutoUpdateCountryCode) {
    autoUpdateCountryCodeFromPhoneNumber()
  }

  emits('update', results.value)
}

const emitsValueAndResults = (
  phoneNumber = props.modelValue,
  noAutoUpdateCountryCode?: boolean
) => {
  try {
    getAndEmitResults(phoneNumber, noAutoUpdateCountryCode)

    emitValue(phoneNumber)
  } catch (error) {
    throw new Error(`[BPhoneInput](emitsValueAndResults) ${error}`)
  }
}

const emitValue = (phoneNumber?: string) => {
  formattedNumber.value = sanitizeNumber(phoneNumber)

  const { isValid, e164, formatNational } = getResultsFromPhoneNumber(
    countryCode.value,
    phoneNumber
  )

  const hasDeletedCharac =
    formattedNumber.value &&
    phoneNumber &&
    formattedNumber.value?.length > phoneNumber?.length

  const cursorIsAtEnd =
    phoneNumber && cursorPosition.value
      ? cursorPosition.value + 1 >= phoneNumber.length
      : true

  const shouldUseAsYoutType = (!hasDeletedCharac && cursorIsAtEnd) || isValid

  if (countryCode.value) {
    const isFullNumber = formattedNumber.value?.includes('+')

    formattedNumber.value =
      formatNational && isFullNumber
        ? formatNational
        : shouldUseAsYoutType
        ? getAsYouTypeFormat(countryCode.value, formattedNumber.value)
        : formattedNumber.value
  }

  const valueToEmit = isValid ? e164 : formattedNumber.value

  if (valueToEmit !== props.modelValue) {
    emits('update:modelValue', valueToEmit)
  }
}

const onBlur = () => {
  inputFocused.value = true

  if (countryCode.value) {
    formattedNumber.value = getAsYouTypeFormat(
      countryCode.value,
      formattedNumber.value
    )
  }
}

const setCountryCode = (
  selectedCountryCode: string,
  autoFocusInput = false
) => {
  try {
    const countryAvailable = isCountryAvailable(selectedCountryCode)

    if (countryAvailable) {
      countryCode.value = selectedCountryCode as CountryCode
      emits('countryCode', selectedCountryCode)
      emitsValueAndResults(props.modelValue, true)
    }

    if (autoFocusInput) {
      focusPhoneNumberInput()
      if (formattedNumber.value && formattedNumber.value.includes('+')) {
        formattedNumber.value = undefined
      }
    }
  } catch (error) {
    throw new Error(`[BPhoneInput](setCountryCode) ${error}`)
  }
}

const focusPhoneNumberInput = () => {
  try {
    PhoneNumberInput.value?.$el.querySelector('input')?.focus()
  } catch (error) {
    throw new Error(`[BPhoneInput](focusPhoneNumberInput) ${error}`)
  }
}
</script>
